import DaaSTable, { DaasTableColumn } from '@/components/table/DaasTable';
import { SortDirection, SubSortColumn, sort } from '@/components/table/sortHelper';
import { FinanceModelPcm, FinanceModelTypes } from '@/services/api/contracts/financemodels';
import { Region } from '@/services/api/contracts/region';
import { useRegions } from '@/services/api/hooks/region';
import { FunctionComponent, useMemo, useState } from 'react';
import FinanceModelsRow from './FinanceModelsRow';

export interface FinanceModelsTableData {
  id: string;
  name: string;
  manufacturerName: string;
  categoryName: string;
  term: number;
  type: FinanceModelTypes;
  value: number;
  pcms: FinanceModelPcm[];
  isPeripheral: boolean;
}

interface Props {
  data: FinanceModelsTableData[];
  onEdit: (id: string, isPeripheral: boolean) => void;
  isPeripheral?: boolean;
}

const tableHeaders: DaasTableColumn[] = [
  {
    accessor: 'name',
    text: 'Name',
    sortable: true,
    size: '0.4fr'
  },
  {
    accessor: 'categoryName',
    text: 'Category',
    sortable: true,
    size: '0.7fr'
  },
  {
    accessor: 'manufacturerName',
    text: 'Manufacturer',
    sortable: true,
    size: '0.7fr'
  },
  {
    accessor: 'term',
    text: 'Term',
    sortable: true,
    size: '0.4fr'
  },
  {
    accessor: 'type',
    text: 'Type',
    sortable: true,
    size: '0.4fr'
  },
  {
    accessor: 'pcms',
    subAccessor: 'region',
    text: 'Regional PCM Multiplier',
    sortable: true,
    size: '2fr'
  },
  {
    accessor: '',
    text: 'Edit',
    size: '77px'
  }
];

const processPcmTableHeader = (regions: Region[]) => {
  const pcmHeader = tableHeaders.find(header => header.accessor === 'pcms');
  pcmHeader!.subHeaders = [];
  regions.forEach(region => pcmHeader?.subHeaders?.push(region.code));
};

const FinanceModelsTable: FunctionComponent<Props> = ({ data, onEdit }) => {
  const [sortColumnId, setSortColumnId] = useState<string>(tableHeaders[1].accessor);
  const [sortDirection, setSortDirection] = useState<SortDirection>(SortDirection.ASC);
  const [sortSubColumnId, setSortSubColumnId] = useState<string>();

  const sortedData: FinanceModelsTableData[] = useMemo(() => {
    const header = tableHeaders.find(h => h.accessor === sortColumnId);
    let subSortObj;

    if (header && header.subAccessor) {
      subSortObj = {
        key: header.subAccessor,
        columnId: sortSubColumnId
      } as SubSortColumn;
    }

    // Do a sensible pre-sort of the data- columns 0, 1 and 2 have a lot of repeats.
    // Without this, the data gets randomly re-shuffled within the desired sort.
    let sorted = sort(data, tableHeaders[0].accessor, sortDirection, subSortObj);
    sorted = sort(sorted, tableHeaders[1].accessor, sortDirection, subSortObj);
    sorted = sort(sorted, tableHeaders[2].accessor, sortDirection, subSortObj);

    // Sort again by the thing they actually want.
    return sort(sorted, sortColumnId, sortDirection, subSortObj);
  }, [data, sortColumnId, sortDirection]);

  const [regions] = useRegions();
  processPcmTableHeader(regions);

  return (
    <DaaSTable
      headers={tableHeaders}
      onSortCallback={(columnId, direction, subColumnId) => {
        setSortColumnId(columnId);
        setSortDirection(direction);
        setSortSubColumnId(subColumnId ? subColumnId : undefined);
      }}
      sortColumnId={sortColumnId}
      sortDirection={sortDirection}
      sortSubColumnId={sortSubColumnId}
    >
      {sortedData.map(t => (
        <FinanceModelsRow
          data={t}
          key={t.id}
          onEdit={() => {
            onEdit(t.id, t.isPeripheral);
          }}
        />
      ))}
    </DaaSTable>
  );
};

export default FinanceModelsTable;
